import React, {useMemo, useContext} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {filterToArray} from '../../../lib/objectHelper';

import ArrowLayer from './ArrowLayer';

import {ArrowDirectionType, ArrowLineType, CardType} from '../../../constants/dataEnum';

import {ProtocolEditorContext} from '../ContextProviders/ProtocolEditorContext';
import {LabwareZoomContext} from '../ContextProviders/LabwareZoomContext';

function ArrowLayerContainer({selectedCard, tasks}) {
  const {taskNumberRefs, arrowDetailViewChecked} = useContext(ProtocolEditorContext);
  const {isLabwareZoom} = useContext(LabwareZoomContext) || {};
  const {menuListScrollPosition, zoomWidth, zoomHeight} = useContext(ProtocolEditorContext) || {};

  const arrowDefinitions = useMemo(() => {
    const addDefinition = (i, s, d) => ({
      key: `arrow${i}`,
      fromX: s.x + 15,
      fromY: s.y + 10,
      toX: d.x + 0,
      toY: d.y + 10,
      direction: ArrowDirectionType.LEFT,
      line: ArrowLineType.STRAIGHT,
    });

    if (
      !selectedCard?.tasks ||
      !arrowDetailViewChecked ||
      selectedCard.type === CardType.BROADCAST ||
      selectedCard.type === CardType.POOLING ||
      selectedCard.type === CardType.NORMALIZATION
    ) {
      return [];
    }

    const definition = [];

    if (selectedCard.type === CardType.NORMALIZATION) {
      // const tasksNumberKeys = Object.keys(taskNumberRefs);
      const tasksNumberKeysUnordered = Object.keys(taskNumberRefs);
      const dummy = {};
      tasksNumberKeysUnordered.forEach((t) => {
        const prefix = t.split('-')[0];
        if (!(prefix in dummy)) {
          dummy[prefix] = [];
        }
        dummy[prefix].push(t);
      });
      const tasksNumberKeys = [];
      Object.keys(dummy).forEach((k) => {
        tasksNumberKeys.push(...dummy[k]);
      });

      if (
        tasksNumberKeys.length < 2 ||
        !taskNumberRefs[tasksNumberKeys[0]]?.current ||
        !taskNumberRefs[tasksNumberKeys[1]]?.current
      ) {
        return [];
      }

      const sourcePos = taskNumberRefs[tasksNumberKeys[0]].current.getBoundingClientRect();
      const destPos = taskNumberRefs[tasksNumberKeys[1]].current.getBoundingClientRect();

      definition.push(addDefinition(0, sourcePos, destPos));

      return definition;
    }

    let selectedCardTaskNumber = [];

    if (selectedCard?.tasks) {
      selectedCardTaskNumber = selectedCard.tasks.map(
        (task, index) => `${index + 1}-${tasks ? tasks[task]?.labwareID : ''}`,
      );
    }

    if (selectedCard.type === CardType.SERIAL_DILUTION) {
      selectedCardTaskNumber = filterToArray(selectedCard?.sdRawTasks).map((e) => `${e.number}-${e.labwareID}`);
    }

    const selectedCardTaskNumberRefs = _.pick(taskNumberRefs, selectedCardTaskNumber);
    const tasksNumberKeysUnordered = Object.keys(selectedCardTaskNumberRefs);

    const dummy = {};
    tasksNumberKeysUnordered.forEach((t) => {
      const prefix = t.split('-')[0];
      if (!(prefix in dummy)) {
        dummy[prefix] = [];
      }
      dummy[prefix].push(t);
    });
    const tasksNumberKeys = [];
    Object.keys(dummy).forEach((k) => {
      tasksNumberKeys.push(...dummy[k]);
    });

    if (!tasksNumberKeys.length) {
      return [];
    }

    if (selectedCard.type !== CardType.SERIAL_DILUTION) {
      for (let index = 1; index < tasksNumberKeys.length; index += 1) {
        const sourceKey = tasksNumberKeys[index - 1];
        const destKey = tasksNumberKeys[index];

        if (!taskNumberRefs[sourceKey]?.current || !taskNumberRefs[destKey]?.current) {
          continue;
        }

        const sourcePos = taskNumberRefs[sourceKey].current.getBoundingClientRect();
        const destPos = taskNumberRefs[destKey].current.getBoundingClientRect();
        definition.push(addDefinition(index, sourcePos, destPos));
      }

      return definition;
    }

    for (let index = 1; index < 3; index += 1) {
      const sourceKey = tasksNumberKeys[index - 1];
      const destKey = tasksNumberKeys[index];

      if (!taskNumberRefs[sourceKey]?.current || !taskNumberRefs[destKey]?.current) {
        continue;
      }

      const sourcePos = taskNumberRefs[sourceKey].current.getBoundingClientRect();
      const destPos = taskNumberRefs[destKey].current.getBoundingClientRect();
      definition.push(addDefinition(index, sourcePos, destPos));
    }

    const rootSourceKey = tasksNumberKeys[2];

    for (let index = 3; index < tasksNumberKeys.length; index += 1) {
      const sourceKey = tasksNumberKeys[index];
      const destKey = tasksNumberKeys[index].replace('4', '5');

      if (!taskNumberRefs[rootSourceKey]?.current || !taskNumberRefs[sourceKey]?.current) {
        continue;
      }

      const rootSourcePos = taskNumberRefs[rootSourceKey].current.getBoundingClientRect();
      const sourcePos = taskNumberRefs[sourceKey].current.getBoundingClientRect();
      definition.push(addDefinition(index, rootSourcePos, sourcePos));

      if (!taskNumberRefs[destKey]?.current) {
        continue;
      }

      const destPos = taskNumberRefs[destKey].current.getBoundingClientRect();
      definition.push(addDefinition(`${sourceKey}${destKey}`, sourcePos, destPos));
    }

    return definition;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    arrowDetailViewChecked,
    taskNumberRefs,
    selectedCard?.ID,
    selectedCard?.tasks?.length,
    isLabwareZoom,
    menuListScrollPosition,
    zoomWidth,
    zoomHeight,
  ]);

  return <ArrowLayer arrowDefinitions={arrowDefinitions} id="virtual-deck" zIndex={1} />;
}

ArrowLayerContainer.propTypes = {
  selectedCard: PropTypes.object,
  tasks: PropTypes.object,
};

ArrowLayerContainer.defaultProps = {
  selectedCard: {},
  tasks: {},
};

export default ArrowLayerContainer;
